// src/stores/qr.ts
import { defineStore } from "pinia";
import { ref } from "vue";
import GlobalService from "@/services/global-service";
import SnackbarType from "@/types/enums/snackbar-type";
import EsquireService from "@/services/esquire-service";
import {AuthError} from "@/services/auth-errors";
import useStore from "@/stores/index";

export const useQrStore = defineStore("qrStore", () => {
  const store = useStore();
  const session = store.session;
  const qrStore = store.qr;

  const qrDialogOpen = ref(false);
  const qrLoading = ref(false);
  const qrCodeUrl = ref<string | null>(null);

  async function openQrDialog(): Promise<void> {
    qrDialogOpen.value = true;

    if (qrCodeUrl.value) return;

    qrLoading.value = true;

    try {
      const blob = await qrStore.getReservationQrCode();
      if (!blob) return;
      qrCodeUrl.value = URL.createObjectURL(blob);
    } catch {
      GlobalService.snackbar().show(
        GlobalService.translator().trans("reservation.qr_code.load_failed"),
        SnackbarType.ERROR
      );
    } finally {
      qrLoading.value = false;
    }
  }

  function closeQrDialog(): void {
    qrDialogOpen.value = false;
  }

  function revokeQrCodeUrl(): void {
    if (!qrCodeUrl.value) return;
    URL.revokeObjectURL(qrCodeUrl.value);
    qrCodeUrl.value = null;
  }

  async function getReservationQrCode(): Promise<Blob | null> {
    try {
      return await EsquireService.contracts().getReservationQrCode();
    } catch (e) {
      if (e instanceof AuthError) {
        await session.reAuth();
        return null;
      }

      throw e;
    }
  }

  return {
    qrDialogOpen,
    qrLoading,
    qrCodeUrl,
    openQrDialog,
    closeQrDialog,
    revokeQrCodeUrl,
    getReservationQrCode
  };
});
